import React from 'react';
import { Box, Typography } from 'housecall-ui';

import { useChangeStep } from '../hooks/useChangeStep';
import { useServices } from '../hooks/useServices';
import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';
import { CategoryType, IndustryType, PricingFormType } from '../types/types';

import { CategoryCard } from './CategoryCard';
import PriceFormCard from './PriceFormCard';
import { ScrollableContent } from './ScrollableContent';

const generateDescription = (category: CategoryType): string => {
  const hasSubcategories = Boolean(category.subcategories?.length);
  return (
    (hasSubcategories
      ? category.subcategories?.map((subcategory) => subcategory.name).join(', ')
      : category.services?.map((service) => service.name).join(', ')) || ''
  );
};

export const CategoryList = () => {
  const {
    state: { token },
    dispatch,
    actions,
  } = useOnlineBookingReducer();
  const { data } = useServices({ token });
  const { changeStep } = useChangeStep();

  const clickHandler = (industry: IndustryType, category: CategoryType) => () => {
    const hasSubcategories = Boolean(category.subcategories?.length);
    dispatch(actions.hideWarningMessage());
    dispatch(actions.selectIndustry(industry));
    dispatch(actions.selectCategory(category));
    if (hasSubcategories) {
      if (category.subcategories && category.subcategories.length === 1) {
        dispatch(actions.selectSubCategory(category.subcategories[0]));
        changeStep('services');
      } else {
        changeStep('sub-categories');
      }
    } else {
      changeStep('services');
    }
  };

  const onPricingFormClick = (pricingForm: PricingFormType) => () => {
    dispatch(actions.selectPricingForm(pricingForm));
    changeStep('pricing-form');
  };

  return (
    <ScrollableContent>
      {data?.pricingForms && data.pricingForms.length > 0 ? (
        <Box pt={1.5}>
          {data?.pricingForms?.map((pricingForm) => {
            return (
              <PriceFormCard
                key={pricingForm.uuid}
                pricingForm={pricingForm}
                onFormClick={onPricingFormClick(pricingForm)}
              />
            );
          })}
        </Box>
      ) : null}
      {data?.industries.map((industry) => {
        return (
          <Box key={industry.uuid}>
            <Typography gutterBottom variant="subtitle1">
              {industry.name}
            </Typography>
            {industry.categories.map((category) => (
              <CategoryCard
                key={category.uuid}
                title={category.name}
                description={generateDescription(category)}
                onClick={clickHandler(industry, category)}
              />
            ))}
          </Box>
        );
      })}
    </ScrollableContent>
  );
};
